import { useState, useEffect } from 'react';
import { X, Package, Save, Loader, Bell, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { useModal } from './AppModal';
import { useTheme } from './ThemeContext';

export default function InventoryItemModal({ companyId, locationId, item, onClose, onSave }) {
  const { colors } = useTheme();
  const { showError, showSuccess } = useModal();

  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    part_number: '',
    upc: '',
    reorder_point: 0,
    reorder_quantity: 1,
    unit_cost: 0,
    unit_of_measure: 'each',
    track_inventory: true,
    sku_id: null,
    vendor_id: null
  });
  const [initialQuantity, setInitialQuantity] = useState(0);
  const [currentQuantity, setCurrentQuantity] = useState(0);
  const [availableSkus, setAvailableSkus] = useState([]);
  const [availableVendors, setAvailableVendors] = useState([]);
  const [showAlertOverrides, setShowAlertOverrides] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    use_custom: false,
    warning_threshold: '',
    critical_threshold: '',
    notify_email: true,
    notify_in_app: true
  });
  const [companyDefaults, setCompanyDefaults] = useState({ warning: 1.5, critical: 0.5 });

  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || '',
        description: item.description || '',
        category: item.category || '',
        part_number: item.part_number || '',
        upc: item.upc || '',
        reorder_point: item.reorder_point || 0,
        reorder_quantity: item.reorder_quantity || 1,
        unit_cost: item.unit_cost || 0,
        unit_of_measure: item.unit_of_measure || 'each',
        track_inventory: item.track_inventory !== false,
        sku_id: item.sku_id || null,
        vendor_id: item.vendor_id || null
      });
      const qty = item.inventory_stock?.[0]?.quantity_on_hand || 0;
      setInitialQuantity(qty);
      setCurrentQuantity(qty);
    }
  }, [item]);

  // Load alert config for existing item + company defaults
  useEffect(() => {
    if (companyId) {
      loadCompanyAlertDefaults();
      if (item) loadAlertConfig(item.id);
    }
  }, [companyId, item]);

  const loadCompanyAlertDefaults = async () => {
    try {
      const { data } = await supabaseData
        .from('companies')
        .select('inventory_default_warning_threshold, inventory_default_critical_threshold')
        .eq('id', companyId)
        .single();
      if (data) {
        setCompanyDefaults({
          warning: data.inventory_default_warning_threshold ?? 1.5,
          critical: data.inventory_default_critical_threshold ?? 0.5
        });
      }
    } catch (e) { /* non-critical */ }
  };

  const loadAlertConfig = async (itemId) => {
    try {
      const { data } = await supabaseData
        .from('inventory_alert_configs')
        .select('*')
        .eq('inventory_item_id', itemId)
        .eq('company_id', companyId)
        .single();
      if (data) {
        setAlertConfig({
          use_custom: true,
          warning_threshold: data.warning_threshold ?? '',
          critical_threshold: data.critical_threshold ?? '',
          notify_email: data.notify_email !== false,
          notify_in_app: data.notify_in_app !== false
        });
        setShowAlertOverrides(true);
      }
    } catch (e) { /* no override exists, that's fine */ }
  };

  // Load available SKUs for linking
  useEffect(() => {
    if (companyId) {
      loadSkus();
    }
  }, [companyId]);

  const loadSkus = async () => {
    try {
      const { data } = await supabase
        .from('skus')
        .select('id, code, name')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');
      setAvailableSkus(data || []);
    } catch (e) {
      // Non-critical
    }
  };

  // Load available vendors for linking
  useEffect(() => {
    if (companyId) loadVendors();
  }, [companyId]);

  const loadVendors = async () => {
    try {
      const { data } = await supabase
        .from('vendors')
        .select('id, name, category')
        .eq('company_id', companyId)
        .order('name');
      setAvailableVendors(data || []);
    } catch (e) {
      // Non-critical
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Guard against double-submit (button is disabled, but this catches programmatic/race calls)
    if (saving) return;

    if (!formData.name.trim()) {
      showError('Name is required');
      return;
    }

    setSaving(true);
    try {
      if (item) {
        // Update existing item
        const { error: updateError } = await supabase
          .from('inventory_items')
          .update({
            name: formData.name.trim(),
            description: formData.description.trim(),
            category: formData.category.trim() || null,
            part_number: formData.part_number.trim() || null,
            upc: formData.upc.trim() || null,
            reorder_point: parseInt(formData.reorder_point) || 0,
            reorder_quantity: parseInt(formData.reorder_quantity) || 1,
            unit_cost: parseFloat(formData.unit_cost) || 0,
            unit_of_measure: formData.unit_of_measure || 'each',
            track_inventory: formData.track_inventory,
            sku_id: formData.sku_id || null,
            vendor_id: formData.vendor_id || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', item.id);

        if (updateError) throw updateError;

        // Update stock if quantity changed
        if (currentQuantity !== initialQuantity && locationId) {
          const { data: existingStock } = await supabase
            .from('inventory_stock')
            .select('id')
            .eq('inventory_item_id', item.id)
            .eq('location_id', locationId)
            .single();

          if (existingStock) {
            await supabase
              .from('inventory_stock')
              .update({
                quantity_on_hand: currentQuantity,
                updated_at: new Date().toISOString()
              })
              .eq('id', existingStock.id);
          }

          // Log transaction
          if (currentQuantity !== initialQuantity) {
            await supabase
              .from('inventory_transactions')
              .insert({
                company_id: companyId,
                inventory_item_id: item.id,
                location_id: locationId,
                transaction_type: 'adjustment',
                quantity_change: currentQuantity - initialQuantity,
                quantity_before: initialQuantity,
                quantity_after: currentQuantity,
                notes: 'Manual adjustment from item edit'
              });
          }
        }

        // Save alert config overrides
        await saveAlertConfig(item.id);

        showSuccess('Item updated');
      } else {
        // Create new item
        const { data: newItem, error: createError } = await supabase
          .from('inventory_items')
          .insert({
            company_id: companyId,
            name: formData.name.trim(),
            description: formData.description.trim(),
            category: formData.category.trim() || null,
            part_number: formData.part_number.trim() || null,
            upc: formData.upc.trim() || null,
            reorder_point: parseInt(formData.reorder_point) || 0,
            reorder_quantity: parseInt(formData.reorder_quantity) || 1,
            unit_cost: parseFloat(formData.unit_cost) || 0,
            unit_of_measure: formData.unit_of_measure || 'each',
            track_inventory: formData.track_inventory,
            sku_id: formData.sku_id || null,
            vendor_id: formData.vendor_id || null
          })
          .select()
          .single();

        if (createError) throw createError;

        // Always create stock record if location exists (even with 0 quantity)
        if (locationId) {
          await supabase
            .from('inventory_stock')
            .insert({
              company_id: companyId,
              inventory_item_id: newItem.id,
              location_id: locationId,
              quantity_on_hand: currentQuantity
            });

          // Log initial stock if quantity > 0
          if (currentQuantity > 0) {
            await supabase
              .from('inventory_transactions')
              .insert({
                company_id: companyId,
                inventory_item_id: newItem.id,
                location_id: locationId,
                transaction_type: 'adjustment',
                quantity_change: currentQuantity,
                quantity_before: 0,
                quantity_after: currentQuantity,
                notes: 'Initial stock on item creation'
              });
          }
        }

        // Save alert config overrides for new item
        await saveAlertConfig(newItem.id);

        showSuccess('Item created');
      }

      onSave();
    } catch (error) {
      console.error('Error saving item:', error);
      showError('Failed to save item: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const saveAlertConfig = async (itemId) => {
    if (!alertConfig.use_custom) {
      // Remove any existing override
      await supabase
        .from('inventory_alert_configs')
        .delete()
        .eq('inventory_item_id', itemId)
        .eq('company_id', companyId);
      return;
    }

    const configData = {
      company_id: companyId,
      inventory_item_id: itemId,
      warning_threshold: alertConfig.warning_threshold !== '' ? parseFloat(alertConfig.warning_threshold) : null,
      critical_threshold: alertConfig.critical_threshold !== '' ? parseFloat(alertConfig.critical_threshold) : null,
      notify_email: alertConfig.notify_email,
      notify_in_app: alertConfig.notify_in_app
    };

    // Upsert
    const { data: existing } = await supabase
      .from('inventory_alert_configs')
      .select('id')
      .eq('inventory_item_id', itemId)
      .eq('company_id', companyId)
      .single();

    if (existing) {
      await supabase
        .from('inventory_alert_configs')
        .update(configData)
        .eq('id', existing.id);
    } else {
      await supabase
        .from('inventory_alert_configs')
        .insert(configData);
    }
  };

  const commonCategories = [
    'Parts',
    'Materials',
    'Tools',
    'Supplies',
    'Equipment',
    'Consumables',
    'Safety',
    'Other'
  ];

  const unitOptions = [
    'each',
    'box',
    'case',
    'pack',
    'roll',
    'gallon',
    'quart',
    'pint',
    'ounce',
    'pound',
    'foot',
    'yard',
    'meter'
  ];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: colors.bgCard,
          borderRadius: '16px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Package size={24} color={colors.accent} />
            <h2 style={{ margin: 0, color: colors.text, fontSize: '1.25rem' }}>
              {item ? 'Edit Item' : 'Add Item'}
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: colors.textMuted,
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
          {/* Name */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: colors.text,
              fontWeight: 'bold'
            }}>
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Item name"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '1rem'
              }}
            />
          </div>

          {/* Category & Part Number Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            marginBottom: '1.25rem'
          }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: colors.textMuted,
                fontSize: '0.9rem'
              }}>
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text
                }}
              >
                <option value="">Select...</option>
                {commonCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: colors.textMuted,
                fontSize: '0.9rem'
              }}>
                Part Number
              </label>
              <input
                type="text"
                value={formData.part_number}
                onChange={(e) => handleChange('part_number', e.target.value)}
                placeholder="PN-12345"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text
                }}
              />
            </div>
          </div>

          {/* Description */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: colors.textMuted,
              fontSize: '0.9rem'
            }}>
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Optional description"
              rows={2}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                resize: 'vertical'
              }}
            />
          </div>

          {/* Link to Product (SKU) */}
          {availableSkus.length > 0 && (
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: colors.textMuted,
                fontSize: '0.9rem'
              }}>
                Link to Product (for auto-deduct)
              </label>
              <select
                value={formData.sku_id || ''}
                onChange={(e) => handleChange('sku_id', e.target.value || null)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text
                }}
              >
                <option value="">None — standalone inventory item</option>
                {availableSkus.map(sku => (
                  <option key={sku.id} value={sku.id}>
                    {sku.name}{sku.code ? ` (${sku.code})` : ''}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: '0.25rem' }}>
                Links this inventory item to a product so stock deducts when jobs use it
              </div>
            </div>
          )}

          {/* Vendor */}
          {availableVendors.length > 0 && (
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: colors.textMuted,
                fontSize: '0.9rem'
              }}>
                Vendor / Supplier
              </label>
              <select
                value={formData.vendor_id || ''}
                onChange={(e) => handleChange('vendor_id', e.target.value || null)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text
                }}
              >
                <option value="">No vendor</option>
                {availableVendors.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.name}{v.category ? ` (${v.category})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Current Quantity */}
          <div style={{
            background: colors.bgInput,
            borderRadius: '12px',
            padding: '1rem',
            marginBottom: '1.25rem'
          }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: colors.text,
              fontWeight: 'bold'
            }}>
              Current Quantity on Hand
            </label>
            <input
              type="number"
              min="0"
              value={currentQuantity}
              onChange={(e) => setCurrentQuantity(parseInt(e.target.value) || 0)}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: colors.bgCard,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '1.25rem',
                fontWeight: 'bold',
                textAlign: 'center'
              }}
            />
          </div>

          {/* Unit Cost & Unit of Measure */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            marginBottom: '1.25rem'
          }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: colors.textMuted,
                fontSize: '0.9rem'
              }}>
                Unit Cost
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute',
                  left: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: colors.textMuted
                }}>$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.unit_cost}
                  onChange={(e) => handleChange('unit_cost', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 0.75rem 0.75rem 1.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text
                  }}
                />
              </div>
            </div>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: colors.textMuted,
                fontSize: '0.9rem'
              }}>
                Unit of Measure
              </label>
              <select
                value={formData.unit_of_measure}
                onChange={(e) => handleChange('unit_of_measure', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text
                }}
              >
                {unitOptions.map(unit => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Reorder Settings */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: colors.textMuted,
                fontSize: '0.9rem'
              }}>
                Reorder Point
              </label>
              <input
                type="number"
                min="0"
                value={formData.reorder_point}
                onChange={(e) => handleChange('reorder_point', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text
                }}
              />
              <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: '0.25rem' }}>
                Alert when stock falls below this
              </div>
            </div>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: colors.textMuted,
                fontSize: '0.9rem'
              }}>
                Reorder Quantity
              </label>
              <input
                type="number"
                min="1"
                value={formData.reorder_quantity}
                onChange={(e) => handleChange('reorder_quantity', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text
                }}
              />
              <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: '0.25rem' }}>
                Suggested order quantity
              </div>
            </div>
          </div>

          {/* Alert Overrides (collapsible) */}
          <div style={{
            marginBottom: '1.5rem',
            border: `1px solid ${colors.border}`,
            borderRadius: '10px',
            overflow: 'hidden'
          }}>
            <button
              type="button"
              onClick={() => setShowAlertOverrides(!showAlertOverrides)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                background: colors.bgInput,
                border: 'none',
                color: colors.text,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '0.9rem',
                fontWeight: '600'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Bell size={16} color={colors.accent} />
                Alert Overrides
                {alertConfig.use_custom && (
                  <span style={{
                    fontSize: '0.7rem',
                    background: colors.accent + '20',
                    color: colors.accent,
                    padding: '1px 6px',
                    borderRadius: '4px',
                    fontWeight: 'bold'
                  }}>CUSTOM</span>
                )}
              </div>
              {showAlertOverrides ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>

            {showAlertOverrides && (
              <div style={{ padding: '1rem' }}>
                {/* Toggle custom alerts */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '1rem'
                }}>
                  <div>
                    <div style={{ color: colors.text, fontSize: '0.9rem', fontWeight: '500' }}>
                      Use custom thresholds
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: '0.75rem' }}>
                      Override company defaults for this item
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAlertConfig(prev => ({ ...prev, use_custom: !prev.use_custom }))}
                    style={{
                      width: '44px',
                      height: '24px',
                      borderRadius: '12px',
                      border: 'none',
                      background: alertConfig.use_custom ? colors.accent : colors.border,
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'background 0.2s'
                    }}
                  >
                    <div style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      background: '#fff',
                      position: 'absolute',
                      top: '3px',
                      left: alertConfig.use_custom ? '23px' : '3px',
                      transition: 'left 0.2s'
                    }} />
                  </button>
                </div>

                {alertConfig.use_custom && (
                  <>
                    {/* Threshold inputs */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '1rem',
                      marginBottom: '1rem'
                    }}>
                      <div>
                        <label style={{
                          display: 'block',
                          marginBottom: '0.5rem',
                          color: colors.warning,
                          fontSize: '0.8rem',
                          fontWeight: '600'
                        }}>
                          Warning Threshold
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={alertConfig.warning_threshold}
                          onChange={(e) => setAlertConfig(prev => ({ ...prev, warning_threshold: e.target.value }))}
                          placeholder={String(companyDefaults.warning)}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: colors.bgInput,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '8px',
                            color: colors.text
                          }}
                        />
                        <div style={{ fontSize: '0.7rem', color: colors.textMuted, marginTop: '0.25rem' }}>
                          Multiplier of reorder point (default: {companyDefaults.warning}x)
                        </div>
                      </div>
                      <div>
                        <label style={{
                          display: 'block',
                          marginBottom: '0.5rem',
                          color: colors.error,
                          fontSize: '0.8rem',
                          fontWeight: '600'
                        }}>
                          Critical Threshold
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={alertConfig.critical_threshold}
                          onChange={(e) => setAlertConfig(prev => ({ ...prev, critical_threshold: e.target.value }))}
                          placeholder={String(companyDefaults.critical)}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: colors.bgInput,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '8px',
                            color: colors.text
                          }}
                        />
                        <div style={{ fontSize: '0.7rem', color: colors.textMuted, marginTop: '0.25rem' }}>
                          Multiplier of reorder point (default: {companyDefaults.critical}x)
                        </div>
                      </div>
                    </div>

                    {/* Preview */}
                    {parseInt(formData.reorder_point) > 0 && (
                      <div style={{
                        background: colors.bgInput,
                        borderRadius: '8px',
                        padding: '0.75rem',
                        marginBottom: '1rem',
                        fontSize: '0.8rem',
                        color: colors.textMuted
                      }}>
                        With reorder point of <strong style={{ color: colors.text }}>{formData.reorder_point}</strong>:
                        <div style={{ marginTop: '0.25rem' }}>
                          <span style={{ color: colors.warning }}>Warning</span> at {Math.round(
                            (alertConfig.warning_threshold || companyDefaults.warning) * parseInt(formData.reorder_point)
                          )} units
                          {' | '}
                          <span style={{ color: colors.error }}>Critical</span> at {Math.round(
                            (alertConfig.critical_threshold || companyDefaults.critical) * parseInt(formData.reorder_point)
                          )} units
                        </div>
                      </div>
                    )}

                    {/* Notification preferences */}
                    <div style={{ display: 'flex', gap: '1.5rem' }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: colors.text,
                        fontSize: '0.85rem',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={alertConfig.notify_email}
                          onChange={(e) => setAlertConfig(prev => ({ ...prev, notify_email: e.target.checked }))}
                        />
                        Email alerts
                      </label>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: colors.text,
                        fontSize: '0.85rem',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={alertConfig.notify_in_app}
                          onChange={(e) => setAlertConfig(prev => ({ ...prev, notify_in_app: e.target.checked }))}
                        />
                        In-app alerts
                      </label>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={saving}
            style={{
              width: '100%',
              padding: '1rem',
              background: colors.accent,
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontWeight: 'bold',
              fontSize: '1rem',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            {saving ? (
              <>
                <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                Saving...
              </>
            ) : (
              <>
                <Save size={18} />
                {item ? 'Update Item' : 'Create Item'}
              </>
            )}
          </button>
        </form>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
